// Access gating for the KAT-230 wind-down.
//
// Reads the `access` block from the env config injected at startup by
// scripts/inject-config.cjs. Every flag defaults to OPEN when absent, so an
// env file without the block behaves exactly as it did before.
//
// Nothing here removes a feature — it only decides whether the door is open.
// Flags are flipped per environment in web/env/<env>/config.json.

import config from '../config.generated.json' assert { type: "json" };
import { useInstanceStore } from '../stores/UseInstanceStore';

const DEFAULTS = {
  login: true,           // wallet auth: header control, Hero CTA, login()
  restoreSession: true,  // silent re-auth for browsers holding a JWT
  product: true,         // /dashboard, /workspace, legacy redirects
  checkout: true,        // upgrade / extend / downgrade on /plans
};

// Zustand persist key — see stores/UseInstanceStore.jsx
const SESSION_STORAGE_KEY = 'tubman-bobtail-instance';
const FIRST_LOGIN_KEY = 'katbot_first_login_seen';

// Resolved once: config.generated.json is static for the life of the page.
const ACCESS = Object.freeze({
  login: config?.access?.login ?? DEFAULTS.login,
  restoreSession: config?.access?.restoreSession ?? DEFAULTS.restoreSession,
  product: config?.access?.product ?? DEFAULTS.product,
  checkout: config?.access?.checkout ?? DEFAULTS.checkout,
});

export function getAccess() {
  return ACCESS;
}

/**
 * One-time purge of the persisted session once sessions are closed.
 *
 * Clears in-memory state first so zustand's persist middleware writes back a
 * token-less snapshot, then drops the storage key outright. Call before the
 * React tree mounts so no authenticated UI renders on the way out.
 *
 * No-op while restoreSession is open. Returns true if a purge happened.
 */
export function purgeStoredSession() {
  if (ACCESS.restoreSession) return false;

  try {
    const state = useInstanceStore.getState();
    state.clearAuthTokens?.();
    state.clearUserSub?.();
    window.localStorage.removeItem(SESSION_STORAGE_KEY);
    window.localStorage.removeItem(FIRST_LOGIN_KEY);
    console.log('[Access] Sessions are closed — stored session purged.');
    return true;
  } catch (err) {
    console.warn('[Access] Could not purge stored session:', err?.message);
    return false;
  }
}
